import { PurchaseItemType } from '../../../api/dealer-api-interface-all';
import type { FabricationPurchaseOrderItem, PurchaseOrderItemSource } from '../../../api/dealer-api-interface-all';

// Label resolvers are injected (rather than imported) so this module stays free of
// UI/localization runtime imports and is testable in isolation. The table passes the
// real getMaterialTypeLabel / getSourceLabel.
export interface FilterSortPurchaseOrderItemsOptions {
  filterTerm?: string | null;
  sortField?: string;
  sortAscending?: boolean;
  typeLabel: (t: PurchaseItemType) => string;
  sourceLabel: (s: PurchaseOrderItemSource) => string;
  /** When true, Supplier rows appear before External rows regardless of any other sort criterion. */
  groupBySource?: boolean;
  /** When set, only rows of this material type are kept; null/undefined keeps all types. */
  materialTypeFilter?: PurchaseItemType | null;
}

// Case- and accent-insensitive text compare, treating null/undefined as empty.
function compareText(a: string | null | undefined, b: string | null | undefined): number {
  return (a ?? '').localeCompare(b ?? '', undefined, { sensitivity: 'base' });
}

// Display strings for the grid's derived columns. These live here, not in the table,
// so the rendered text and the filter's search corpus come from one source and cannot
// drift apart.

/** Dimensions cell: bar length for extrusions, "width x height" for fills, else empty. */
export function purchaseOrderItemDimensionsText(item: FabricationPurchaseOrderItem): string {
  if (item.itemType === PurchaseItemType.Extrusion) return item.barLengthDisplay ?? '';
  if (item.itemType === PurchaseItemType.Fill) {
    if (item.widthDisplay == null && item.heightDisplay == null) return '';
    return `${item.widthDisplay ?? ''} x ${item.heightDisplay ?? ''}`;
  }
  return '';
}

/**
 * How a line's quantity relates to its purchasing unit: whole packs (component sold in packs),
 * whole rolls (gasket), a component not sold in packs (noPack), or a gasket not supplied in a
 * roll (noRoll). Returns null when the concept does not apply (extrusions, fills). This is the
 * single source of the predicate for both the grid hint text and the info-icon tooltip in the
 * table.
 *
 * `detail` is what one pack/roll holds, ready to display (pack size, or the roll's formatted
 * length), resolved here so the view need not know which item field carries it; it is absent
 * for `noPack`/`noRoll`, which have nothing to quantify.
 */
export type PurchaseOrderItemPackRoll =
  | { kind: 'pack'; count: number; detail: string }
  | { kind: 'roll'; count: number; detail: string }
  | { kind: 'noPack' }
  | { kind: 'noRoll' };

export function purchaseOrderItemPackRoll(item: FabricationPurchaseOrderItem): PurchaseOrderItemPackRoll | null {
  if (item.itemType === PurchaseItemType.Component) {
    // Pack size 1 (or unset) means the component is sold individually, not in packs.
    if (item.packSize && item.packSize > 1) {
      return { kind: 'pack', count: Math.ceil(item.quantity / item.packSize), detail: `${item.packSize}` };
    }
    return { kind: 'noPack' };
  }
  if (item.itemType === PurchaseItemType.Gasket) {
    // No (or zero) roll length means the gasket is not supplied in a roll.
    if (item.rollLength && item.rollLength > 0) {
      return {
        kind: 'roll',
        // Round before the ceiling: quantity is an accumulated sum of doubles, so an exact multiple
        // of the roll length can float a hair above it and Math.ceil would show one roll too many.
        // Mirrors the BackOffice RollCount so the "(N rolls)" hint matches the ordered quantity.
        count: Math.ceil(Math.round((item.quantity / item.rollLength) * 1e6) / 1e6),
        detail: item.rollLengthDisplay ?? `${item.rollLength}`
      };
    }
    return { kind: 'noRoll' };
  }
  return null;
}

/** Quantity-cell suffix: pack count for components, roll count for gaskets, else empty. */
export function purchaseOrderItemPackRollHint(item: FabricationPurchaseOrderItem): string {
  const packRoll = purchaseOrderItemPackRoll(item);
  if (packRoll?.kind === 'pack') return ` (${packRoll.count} packs)`;
  if (packRoll?.kind === 'roll') return ` (${packRoll.count} rolls)`;
  return '';
}

// The lower-cased concatenation of every value the user can see in a row, used for the
// free-text "contains" filter. Mirrors the grid columns - including the derived
// dimensions and pack/roll hint - so a search like "204 packs" finds its row.
function searchText(
  item: FabricationPurchaseOrderItem,
  typeLabel: (t: PurchaseItemType) => string,
  sourceLabel: (s: PurchaseOrderItemSource) => string
): string {
  const code = `${item.libraryCode ? `${item.libraryCode} ` : ''}${item.code}`;
  return [
    typeLabel(item.itemType),
    code,
    item.resourceDescription,
    item.finish,
    item.finishDescription,
    purchaseOrderItemDimensionsText(item),
    `${item.quantityDisplay ?? item.quantity}`,
    purchaseOrderItemPackRollHint(item),
    sourceLabel(item.source)
  ]
    .filter(v => !!v)
    .join(' ')
    .toLowerCase();
}

/**
 * Returns a new array: the items filtered by a case-insensitive "contains" match
 * over their visible text, then sorted.
 *
 * Sort fields: 'description' (V6 resource description), 'finish' (finish code),
 * 'netSellPrice' (supplier price, only shown once issued), or anything else (default)
 * which sorts by type label and then uses the description as a stable secondary order
 * within each type. The secondary description order is always ascending so rows stay
 * grouped sensibly even when the type order is reversed.
 */
export function filterAndSortPurchaseOrderItems(
  items: FabricationPurchaseOrderItem[],
  options: FilterSortPurchaseOrderItemsOptions
): FabricationPurchaseOrderItem[] {
  const { typeLabel, sourceLabel } = options;
  let rows = [...items];

  if (options.materialTypeFilter != null) {
    rows = rows.filter(item => item.itemType === options.materialTypeFilter);
  }

  const term = (options.filterTerm ?? '').trim().toLowerCase();
  if (term) {
    rows = rows.filter(item => searchText(item, typeLabel, sourceLabel).includes(term));
  }

  const sortField = options.sortField ?? 'itemType';
  const direction = (options.sortAscending ?? true) ? 1 : -1;

  rows.sort((a, b) => {
    // Source grouping is a fixed primary key: Supplier (1) always sorts before External (2).
    if (options.groupBySource) {
      const bySource = a.source - b.source;
      if (bySource !== 0) return bySource;
    }

    if (sortField === 'description') return compareText(a.resourceDescription, b.resourceDescription) * direction;
    if (sortField === 'finish') return compareText(a.finish, b.finish) * direction;
    if (sortField === 'netSellPrice') return (a.netSellPrice - b.netSellPrice) * direction;

    const byType = compareText(typeLabel(a.itemType), typeLabel(b.itemType)) * direction;
    if (byType !== 0) return byType;
    return compareText(a.resourceDescription, b.resourceDescription);
  });

  return rows;
}
